import { Injectable, computed, signal } from '@angular/core';

/**
 * Time-Travel Service.
 *
 * Holds the user-chosen "as of" date. When set, the entire app behaves as
 * though today were that date — date-range presets, AR aging cutoffs,
 * `@dateOfRun` / `@d90Cutoff` / `@ly0..@ly3` partition pruning, gold-doc
 * anchor dates, etc. — all derive from this single source.
 *
 * Persisted to **sessionStorage** intentionally: clears on tab close so a
 * user who time-travelled, walked away, and came back tomorrow doesn't read
 * stale-from-the-future numbers in a fresh session.
 *
 * Null = real time (system clock). The whole feature is opt-in.
 */
@Injectable({ providedIn: 'root' })
export class AsOfDateService {
    private static readonly STORAGE_KEY = 'tma:as-of-date';

    /** YYYY-MM-DD when set, null = use real time. */
    private readonly _asOf = signal<string | null>(this.loadFromSession());

    /** Read the current override. Null = real time. */
    readonly asOfDate = this._asOf.asReadonly();

    /** True when user is in time-travel mode (banner should be visible). */
    readonly isTimeTravel = computed(() => this._asOf() !== null);

    /** Set or update the override. Pass null (or call clear()) to reset. */
    set(date: string | null): void {
        if (!date) {
            this.clear();
            return;
        }
        // Normalize to YYYY-MM-DD even if caller sent ISO with time portion.
        const dateOnly = date.split('T')[0];
        this._asOf.set(dateOnly);
        try {
            sessionStorage.setItem(AsOfDateService.STORAGE_KEY, dateOnly);
        } catch {
            // sessionStorage can throw in private mode — silent fallback to in-memory.
        }
    }

    /** Reset to real time. */
    clear(): void {
        this._asOf.set(null);
        try {
            sessionStorage.removeItem(AsOfDateService.STORAGE_KEY);
        } catch {
            /* ignore */
        }
    }

    /**
     * Effective "now" — returns the override as a Date when set, otherwise
     * the real system clock. Use this everywhere instead of `new Date()`
     * when you need to compute date-range presets, aging cutoffs, etc.
     */
    now(): Date {
        const override = this._asOf();
        if (!override) return new Date();
        // Anchor at end-of-day local time so ranges that go "from today
        // backward" aren't off-by-one.
        const [y, m, d] = override.split('-').map(Number);
        const out = new Date(y, m - 1, d);
        out.setHours(23, 59, 59, 999);
        return out;
    }

    /**
     * ISO YYYY-MM-DD of the effective "today". Useful for sending as
     * `dateOfRun` / `anchorDate` on backend requests.
     */
    nowIso(): string {
        const o = this._asOf();
        if (o) return o;
        const n = new Date();
        const yy = n.getFullYear();
        const mm = String(n.getMonth() + 1).padStart(2, '0');
        const dd = String(n.getDate()).padStart(2, '0');
        return `${yy}-${mm}-${dd}`;
    }

    private loadFromSession(): string | null {
        try {
            const v = sessionStorage.getItem(AsOfDateService.STORAGE_KEY);
            if (!v) return null;
            // Defensive: only accept YYYY-MM-DD
            return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
        } catch {
            return null;
        }
    }
}
